import { useTranslation } from 'react-i18next';
import { useState, useEffect } from "react";
import { useDispatch, useSelector } from 'react-redux';

import {
    Grid, FormGroup, Button, Dialog, DialogTitle, DialogContent,
    DialogActions, Paper, Typography, Divider, Chip, Box, IconButton
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import EventIcon from '@mui/icons-material/Event';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import MeetingRoomOutlinedIcon from '@mui/icons-material/MeetingRoomOutlined';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

import { APPOINTMENT_TYPES, FREIGHT_AREAS, FREIGHT_AREAS_ANG_GATES, ROLES, OPERATION_3020_GATE_MATCHINGS, freightAreaRequiresGate } from '../../utils/constants'
import MainCard from '../../UI/MainCard';
import CustomTextField from '../../UI/CustomTextField';
import YellowButton from '../../UI/YellowButton';
import { hidePopup, setMessageAndButtonText } from "../../redux/popup"
import AppointmentService from '../../services/AppointmentService'
import CustomDropdown from '../../UI/CustomDropdown';
import { showSuccess } from '../../redux/snackbar';
import { modifyPlate } from '../../utils/modifyPlate';
import NavigationPanel from '../../UI/NavigationPanel';
import LoadReservationCalendarPanel from './LoadReservationCalendarPanel';

import dayjs from 'dayjs';

import CompanyService from '../../services/CompanyService';
import ConfigurationService from '../../services/ConfigurationService';

import { removeSpaces, isFreightSlotSelectableAtNow } from '../../utils/helper';

const LoadReservation = () => {
    const user = useSelector(state => state.user)
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();

    const [phoneNumber, setPhoneNumber] = useState();
    const [plate, setPlate] = useState("");
    const [trailerNumber, setTrailerNumber] = useState("");
    const [appointmentDate, setAppointmentDate] = useState(dayjs());
    const [gateNumber, setGateNumber] = useState("");
    const [referenceNumber, setReferenceNumber] = useState("");
    const [availableGates, setAvailableGates] = useState([])
    const [selectedSlot, setSelectedSlot] = useState({});
    const [availableSlots, setAvailableSlots] = useState([])
    const [selectedCompany, setSelectedCompany] = useState();
    const [companyList, setCompanyList] = useState([]);
    const [freightArea, setFreightArea] = useState("");
    const [description, setDescription] = useState("");
    const [panels] = useState([t('adminPanel'), t('freightOperations'), t('bookANewFreightAppointment')])
    const [companySearchParams, setCompanySearchParams] = useState({})
    const [companySearchParamsLength, setCompanySearchParamsLength] = useState(0)
    const [config, setConfig] = useState({})
    const [freightAppointmentWarning, setFreightAppointmentWarning] = useState('')
    const [showCalendar, setShowCalendar] = useState(false)

    const appointmentService = new AppointmentService()
    const companyService = new CompanyService()
    const configurationService = new ConfigurationService()

    useEffect(() => {
        if (!showCalendar) return
        if (dayjs(appointmentDate).startOf('day').isBefore(dayjs().startOf('day'))) {
            setAppointmentDate(dayjs())
            setSelectedSlot({})
        }
    }, [showCalendar])

    useEffect(() => {
        if (user.type === ROLES.PARTNER_COMPANY_AUTHORIZED_USER) {
            setPhoneNumber(user.company.phone)
            setSelectedCompany({ id: user.companyId, title: user.company.name, phone: user.company.phone })
        } else
            fetchCompanies()
        fetchConfig()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, i18n.language])

    useEffect(() => {
        setPhoneNumber(selectedCompany?.phone)
    }, [selectedCompany])

    useEffect(() => {
        if (freightArea) {
            setGateNumber('')
            setSelectedSlot('')
            setAvailableGates(FREIGHT_AREAS_ANG_GATES[freightArea])
        }
    }, [freightArea])

    useEffect(() => {
        if (companySearchParams && companySearchParamsLength >= 2) {
            fetchCompanies({ search: true, query: companySearchParams })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companySearchParams, companySearchParamsLength])

    useEffect(() => {
        if (selectedSlot && selectedSlot.SLOT_NUMBER && Array.isArray(availableSlots) && availableSlots.length > 0) {
            const match = availableSlots.find(s => s.SLOT_NUMBER === selectedSlot.SLOT_NUMBER)
            if (match && match !== selectedSlot) setSelectedSlot(match)
        }
    }, [availableSlots])

    const fetchConfig = async () => {
        const result = await configurationService.getConfiguration()
        if (result) {
            setConfig(result.data)
            const lang = i18n.language || 'tr'
            setFreightAppointmentWarning(result.data.freightAppointmentWarning?.[lang] || "")
        }
    }

    const clearFields = async () => {
        setFreightArea('')
        setAvailableSlots([])
        setPlate('')
        setTrailerNumber('')
        setDescription('')
        setGateNumber('')
        setSelectedSlot({})
    }

    const createAppointment = async (appointment) => {
        const result = await appointmentService.createAppointment(appointment)
        if (result) {
            dispatch(showSuccess({ message: t("successMessage") }));
            clearFields()
        } else {
            dispatch(setMessageAndButtonText({
                title: t("errorMessageTitle"),
                text: 'Randevu Oluşturulamadı!',
                noButtonText: t("commonOkButton"),
                noButtonAction: () => { dispatch(hidePopup()) },
                isErrorMessage: true
            }))
        }
    }

    const handleSubmit = async () => {
        const appointmentCreateObject = {
            appointmentType: APPOINTMENT_TYPES.OPERATION,
            companyId: selectedCompany?.id,
            phoneNumber,
            freightArea,
            plate: removeSpaces(plate),
            trailerNumber: removeSpaces(trailerNumber),
            appointmentDate: dayjs(appointmentDate).format('YYYY-MM-DD'),
            appointmentTime: selectedSlot?.START,
            // 3020 ve MAİS kapısı takvimdeki Select ile gateNumber state'inde; açıklama alanı buna karışmamalı (parseInt(description) → NaN / yanlış kapı).
            gateNumber:
                freightAreaRequiresGate(freightArea) && gateNumber !== '' && gateNumber != null
                    ? Number(gateNumber)
                    : undefined,
            slotNumber: selectedSlot?.SLOT_NUMBER,
            referenceNumber,
            description
        }

        const dateISO = appointmentCreateObject.appointmentDate
        if (!isFreightSlotSelectableAtNow(dateISO, selectedSlot)) {
            dispatch(setMessageAndButtonText({
                title: t('errorMessageTitle'),
                text: t('pastAppointmentDateOrTime'),
                noButtonText: t('commonOkButton'),
                noButtonAction: () => { dispatch(hidePopup()) },
                isErrorMessage: true
            }))
            return
        }

        if (validateAppointmentObject(appointmentCreateObject)) {
            await createAppointment(appointmentCreateObject)
        } else {
            dispatch(setMessageAndButtonText({
                title: t("errorMessageTitle"),
                text: t("errorMessage"),
                noButtonText: t("commonOkButton"),
                noButtonAction: () => { dispatch(hidePopup()) },
                isErrorMessage: true
            }))
        }
    }

    const validateAppointmentObject = (obj) => {
        const missingStr = (v) => v === undefined || v === null || String(v).trim() === ''
        if (missingStr(obj.appointmentType)) return false
        if (missingStr(obj.companyId)) return false
        if (missingStr(obj.phoneNumber)) return false
        if (missingStr(obj.freightArea)) return false
        if (missingStr(obj.plate)) return false
        if (missingStr(obj.appointmentDate)) return false
        if (missingStr(obj.appointmentTime)) return false
        if (obj.slotNumber === undefined || obj.slotNumber === null) return false
        if (freightAreaRequiresGate(obj.freightArea)) {
            const g = obj.gateNumber
            if (g === undefined || g === null || g === '' || !Number.isFinite(Number(g))) return false
        }
        return true
    }

    const fetchCompanies = async (params) => {
        const result = await companyService.getCompanies(params)
        if (result) setCompanyList(result.data)
    }

    const companyField = () => {
        if (user.type === ROLES.PARTNER_COMPANY_AUTHORIZED_USER) {
            return (
                <CustomTextField
                    label={t('companyWhichBook')}
                    placeholder={t('companyWhichBook')}
                    value={selectedCompany?.title}
                    disabled={true}
                />
            )
        }
        return (
            <CustomDropdown
                label={t('companyWhichBook')}
                placeholder={t('companyWhichBook')}
                value={selectedCompany}
                setValue={setSelectedCompany}
                searchParams={companySearchParams}
                setSearchParams={setCompanySearchParams}
                setSearchParamsLength={setCompanySearchParamsLength}
                fetchFunction={fetchCompanies}
                data={companyList.map((c) => ({ id: c.id, title: c.name, phone: c.phone }))}
            />
        )
    }

    const hasSlotSelected = appointmentDate && selectedSlot?.START
    const gate3020Label = freightArea === FREIGHT_AREAS[3020]
        ? (() => {
            const key = Object.keys(OPERATION_3020_GATE_MATCHINGS).find(k => OPERATION_3020_GATE_MATCHINGS[k] === Number(gateNumber))
            return key ? ` (${t(key)})` : ''
        })()
        : ''

    return (<>
        <MainCard isSingleCard={true}>
            {/* Breadcrumbs */}
            <Grid container item xs={12} sx={{ mb: 1 }}>
                <NavigationPanel panels={panels} />
            </Grid>

            {/* Info bar + Calendar button */}
            <Grid container item xs={12} sx={{
                backgroundColor: '#EDF4FC',
                borderRadius: '12px',
                p: '12px 16px',
                mb: 2.5,
                alignItems: 'center',
                justifyContent: 'space-between'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <InfoOutlinedIcon sx={{ color: '#1565C0', fontSize: 20 }} />
                    <Typography variant="body2" sx={{ color: '#1565C0', fontWeight: 600 }}>
                        {t('loadReservationNote')}
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    size="small"
                    startIcon={<CalendarMonthIcon />}
                    onClick={() => setShowCalendar(true)}
                    sx={{
                        textTransform: 'none',
                        borderRadius: '8px',
                        fontWeight: 600,
                        boxShadow: 'none',
                        backgroundColor: '#1565C0',
                        '&:hover': { backgroundColor: '#0D47A1', boxShadow: '0 2px 8px rgba(13,71,161,0.3)' }
                    }}
                >
                    {t('switchToCalendarView')}
                </Button>
            </Grid>

            {/* Appointment summary chip when selected */}
            {hasSlotSelected && (
                <Paper variant="outlined" sx={{
                    mb: 2.5,
                    p: '16px 24px',
                    borderRadius: '14px',
                    borderColor: '#C8E6C9',
                    backgroundColor: '#F1F8E9',
                    width: '100%'
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <CheckCircleOutlineIcon sx={{ color: '#2E7D32', fontSize: 20 }} />
                        <Typography variant="subtitle2" sx={{ color: '#2E7D32', fontWeight: 700 }}>
                            {t('appointmentSummary')}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                        <Chip
                            icon={<PlaceOutlinedIcon sx={{ fontSize: 16 }} />}
                            label={t(`${freightArea}`)}
                            size="small"
                            sx={{ fontWeight: 600, backgroundColor: '#fff', border: '1px solid #C8E6C9' }}
                        />
                        {freightAreaRequiresGate(freightArea) && (
                            <Chip
                                icon={<MeetingRoomOutlinedIcon sx={{ fontSize: 16 }} />}
                                label={`${t('gateNumber')}: ${gateNumber}${gate3020Label}`}
                                size="small"
                                sx={{ fontWeight: 600, backgroundColor: '#fff', border: '1px solid #C8E6C9' }}
                            />
                        )}
                        <Chip
                            icon={<EventIcon sx={{ fontSize: 16 }} />}
                            label={dayjs(appointmentDate).format('DD.MM.YYYY')}
                            size="small"
                            sx={{ fontWeight: 700, backgroundColor: '#fff', border: '1px solid #C8E6C9' }}
                        />
                        <Chip
                            icon={<AccessTimeIcon sx={{ fontSize: 16 }} />}
                            label={`${selectedSlot.START} - ${selectedSlot.END}`}
                            size="small"
                            sx={{ fontWeight: 700, backgroundColor: '#fff', border: '1px solid #C8E6C9' }}
                        />
                    </Box>
                </Paper>
            )}

            {/* Form */}
            <FormGroup sx={{ width: '100%' }}>
                <Grid container spacing={2.5}>
                    {/* Row 1 */}
                    <Grid item xs={12} md={6}>
                        {companyField()}
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <CustomTextField
                            label={t('phoneNumberLabel')}
                            value={phoneNumber}
                            setValue={setPhoneNumber}
                            type="tel"
                            placeholder={t('phoneNumberLabel')}
                        />
                    </Grid>

                    {/* Row 2 */}
                    <Grid item xs={12} md={6}>
                        <CustomTextField
                            value={modifyPlate(plate)}
                            label={t('towTruckPlate')}
                            setValue={setPlate}
                            placeholder={t('towTruckPlate')}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <CustomTextField
                            value={modifyPlate(trailerNumber)}
                            label={t('truckPlateLabel')}
                            setValue={setTrailerNumber}
                            placeholder={t('truckPlateLabel')}
                        />
                    </Grid>

                    {/* Row 3: Description full-width */}
                    <Grid item xs={12}>
                        <CustomTextField
                            label={t('description')}
                            value={description}
                            setValue={setDescription}
                            placeholder={t('description')}
                        />
                    </Grid>
                </Grid>

                {/* Warning */}
                {freightAppointmentWarning && (
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mt: 2.5,
                        p: '10px 14px',
                        borderRadius: '10px',
                        backgroundColor: '#FFF8E1',
                        border: '1px solid #FFE082'
                    }}>
                        <WarningAmberRoundedIcon sx={{ color: '#F57C00', fontSize: 20 }} />
                        <Typography variant="body2" sx={{ color: '#E65100', fontWeight: 600 }}>
                            {freightAppointmentWarning}
                        </Typography>
                    </Box>
                )}

                {/* Submit button */}
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <YellowButton
                        type="submit"
                        onClick={handleSubmit}
                        style={{ maxWidth: '320px' }}
                        startIcon={<LocalShippingOutlinedIcon />}
                    >
                        {t('create')}
                    </YellowButton>
                </Box>
            </FormGroup>
        </MainCard>

        {/* Calendar Dialog */}
        <Dialog
            open={showCalendar}
            onClose={() => setShowCalendar(false)}
            fullWidth
            maxWidth="lg"
            PaperProps={{
                sx: { borderRadius: '16px', overflow: 'hidden' }
            }}
        >
            <DialogTitle sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                fontWeight: 700,
                pb: 1
            }}>
                <CalendarMonthIcon sx={{ color: '#1565C0' }} />
                {t('switchToCalendarView')}
            </DialogTitle>
            <Divider />
            <DialogContent sx={{ pt: 2 }}>
                <LoadReservationCalendarPanel
                    t={t}
                    lang={i18n.language}
                    appointmentDate={appointmentDate}
                    setAppointmentDate={setAppointmentDate}
                    freightArea={freightArea}
                    setFreightArea={setFreightArea}
                    gateNumber={gateNumber}
                    setGateNumber={setGateNumber}
                    description={description}
                    setDescription={setDescription}
                    selectedSlot={selectedSlot}
                    setSelectedSlot={setSelectedSlot}
                />
            </DialogContent>
            <Divider />
            <DialogActions sx={{ justifyContent: 'center', py: 2 }}>
                <Button
                    variant="contained"
                    onClick={() => setShowCalendar(false)}
                    sx={{
                        minWidth: '260px',
                        fontWeight: 700,
                        textTransform: 'none',
                        borderRadius: '10px',
                        py: 1.2,
                        boxShadow: 'none',
                        backgroundColor: '#1565C0',
                        '&:hover': { backgroundColor: '#0D47A1' }
                    }}
                >
                    {t('selectAndClose') || 'Seç ve Kapat'}
                </Button>
            </DialogActions>
        </Dialog>
    </>)
}

export default LoadReservation;
